import PaymentMethod from '@alltrails/amplitude/enums/PaymentMethod';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logPurchaseSuccess from '@alltrails/amplitude/events/logPurchaseSuccess';
import setUserProperties from '@alltrails/amplitude/utils/setUserProperties';
import getUserType from '@alltrails/core/utils/getUserType';
import logError from '@alltrails/core/utils/logError';
import { trackTinuitiPurchase } from '@alltrails/core/utils/marketingTracking/tinuitiUtils';
import { trackWeatherPurchase, trackWeatherStartTrial } from '@alltrails/core/utils/marketingTracking/weatherDotComPixel';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type { User } from '@alltrails/types/User';
import { trackPurchaseForTikTok } from '@alltrails/core/utils/marketingTracking/tiktok';
import { trackPurchaseForFacebook } from '@alltrails/core/utils/marketingTracking/facebook';
import { trackPurchaseForReddit } from '@alltrails/core/utils/marketingTracking/reddit';
import getPurchaseScreenTypePlan from './getPurchaseScreenTypePlan';
import { getPriceTotal, isTrial } from './plan';

type Params = {
  userId: number;
  currencyCode: CurrencyCode;
  plan: Plan;
  paymentMethod: PaymentMethod;
  invoiceUuid?: string;
  subscription_event_id?: number;
  coupon?: Coupon;
  user: User;
} & ({ webProPurchaseLocation: 'modal'; trigger: UpsellTrigger } | { webProPurchaseLocation: 'pro page'; trigger?: never });

function handleAttribution({
  userId,
  currencyCode,
  plan,
  webProPurchaseLocation,
  paymentMethod,
  invoiceUuid,
  subscription_event_id,
  coupon,
  trigger,
  user
}: Params) {
  // Only use passed-in userId if there isn't one stored on the current session in Amplitude.
  // Users can purchase on an associated userId without being authenticated through the use of the uid query
  // parameter used on promo emails, and therefore we want to associate the purchase with the correct user id
  // but without opening up our analytics to other weirdness outside of a purchase.
  let existingUserId;

  if (window.amplitude) {
    existingUserId = window.amplitude.getUserId();
  }

  const { recurlyPlanCode, priceTotal } = plan;
  const value = getPriceTotal(plan, coupon);

  // gtag events (not defined in local development)
  if (typeof gtag !== 'undefined') {
    if (!existingUserId) {
      // Set GA user id
      // https://developers.google.com/analytics/devguides/collection/gtagjs/cookies-user-id
      gtag('config', window.ga4_measure_id, {
        user_id: userId
      });
    }
    // Currently, the only purchase option for GA UA config
    gtag('event', 'page_view', { page_path: '/pro/year/success' });
    // GA4
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase
    gtag('event', 'purchase', {
      currency: currencyCode,
      value,
      items: [
        {
          item_id: recurlyPlanCode,
          coupon: coupon?.code,
          discount: plan.priceTotal - value,
          item_category: 'Pro Subscription',
          currency: currencyCode,
          price: priceTotal
        }
      ]
    });
  }

  trackPurchaseForFacebook({
    shouldReInitializePixel: !existingUserId,
    userId,
    emailSha256: user?.emailSha256,
    currencyCode,
    price: isTrial(plan, coupon) ? 0 : value,
    subscription_event_id,
    recurlyPlanCode
  });

  trackPurchaseForReddit({
    userId,
    currencyCode,
    value,
    isTrial: isTrial(plan, coupon),
    subscription_event_id
  });

  // Microsoft UET Pixel
  window.uetq = window.uetq || [];
  window.uetq.push('event', 'purchase', { revenue_value: value, currency: currencyCode });

  // TikTok Pixel
  trackPurchaseForTikTok({
    userId,
    recurlyPlanCode,
    currencyCode,
    value
  });

  // Tinuiti Pixel
  if (existingUserId || userId) {
    const tinuitiUserId = existingUserId || userId;
    trackTinuitiPurchase(value.toString(), tinuitiUserId.toString());
  }

  // Weather.com Pixel
  if (plan.isTrial) {
    trackWeatherStartTrial();
  } else {
    trackWeatherPurchase();
  }

  // Amplitude
  if (!existingUserId) {
    if (window.amplitude) {
      window.amplitude.setUserId(userId.toString());
    }
  }

  logPurchaseSuccess({
    campaign_id: coupon?.code as string, // analytics needs to update purchase success to make this optional
    payment_method: paymentMethod,
    pro_upsell_experiment_variant_name: '', // only required on mobile
    product_id: recurlyPlanCode,
    purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
    purchase_screen_type: undefined, // only required on mobile
    transaction_date: '', // only required on mobile
    transaction_id: '', // only required on mobile
    upsell_trigger: trigger,
    web_purchase_location: webProPurchaseLocation
  });

  // MNTN Conversion Pixel
  try {
    if (process.env.NEXT_PUBLIC_MNTN_PIXEL_ID && window && window.localStorage) {
      const orderId = invoiceUuid ? `web_${invoiceUuid}` : '';
      window.localStorage.setItem('mntn-conversion-data', JSON.stringify({ orderId, totalOrderAmount: value }));
    }
  } catch (e) {
    logError(new Error('Error setting MNTN conversion data', { cause: e }));
  }

  setUserProperties({
    'user type': getUserType(user)
  });
}

export default function handlePaymentFormSuccess(params: Params) {
  // ensure that attribution does not cause any errors
  // that would prevent a user that successfully purchased plus
  // from continuing with the expected flow
  try {
    handleAttribution(params);
  } catch (e) {
    logError(new Error('Error handling payment attribution', { cause: e }));
  }
}
